import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import { Toaster } from 'react-hot-toast';
import 'react-toastify/dist/ReactToastify.css';
import './components/Toast.css'; // 커스텀 토스트 스타일
import './utils/androidBridge'; // Android 브릿지 초기화
import socketService from './services/socketService';
import deepLinkHandler from './utils/deeplink'; // 딥링크 처리기
// Pages
import Login from './pages/Login';
import Home from './pages/Home';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import CustomerAccess from './pages/CustomerAccess';
import AdminDashboard from './pages/AdminDashboard';
import VipDashboard from './pages/VipDashboard';
import Profile from './pages/Profile';
import Notice from './pages/Notice';
import ChangePassword from './pages/ChangePassword';
import Help from './pages/Help';
import NotificationSettings from './pages/NotificationSettings';
import ContractorList from './pages/ContractorList';
import ContractorProfile from './pages/ContractorProfile';
import ContractorStats from './pages/ContractorStats';
import MyContractor from './pages/MyContractor';
import ContractorStatsView from './pages/ContractorStatsView';
import NotificationList from './pages/NotificationList';
import ContractorCommission from './pages/ContractorCommission';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import ContractorApplication from './ContractorApplication';
import AdminContractorApplications from './AdminContractorApplications';

// Components
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true, // 창 포커스 시 새로고침 활성화
      refetchOnMount: true, // 마운트 시 새로고침
      retry: 1,
      staleTime: 30 * 1000, // 30초로 단축 (기존 5분)
      cacheTime: 60 * 1000, // 1분으로 단축 (기존 10분)
    },
  },
});

function App() {
  // 딥링크 자동 실행 제거 - 앱으로 자동 전환하지 않음
  useEffect(() => {
    // 딥링크 생성 함수만 전역 노출 (선택적 - SMS/이메일용)
    window.generateDeepLink = (type, id, params) => {
      return deepLinkHandler.generateShareableLink(type, id, params);
    };
  }, []);

  // 인증 데이터 불일치 감지 및 자동 정리
  useEffect(() => {
    // 특수 URL 접속 패턴 확인 (고객 & 소장)
    const pathname = window.location.pathname;
    const isSpecialAccess = 
      // 고객 URL 패턴
      pathname.match(/^\/\d+\/[\w\d]+$/) ||           // /104/1234
      pathname.match(/^\/h\/\d+\/[\w\d]+$/) ||        // /h/104/1234
      pathname.match(/^\/s\/\d+\/[\w\d]+$/) ||        // /s/104/1234
      // 소장 URL 패턴
      pathname.match(/^\/p\/\d+$/) ||                 // /p/104
      pathname.match(/^\/s\/\d+$/);                   // /s/104
    
    if (isSpecialAccess) {
      console.log('Special URL access detected, skipping auth check');
      return; // 특수 URL 접속은 검사하지 않음
    }
    
    // 인증 데이터 일관성 검사
    const token = localStorage.getItem('token');
    const authStorage = localStorage.getItem('auth-storage');
    
    // 불일치 상황 감지
    let needsClear = false;
    
    if (token && authStorage) {
      try {
        const authData = JSON.parse(authStorage);
        // Zustand의 token과 직접 저장된 token이 다른 경우
        if (authData.state?.token && authData.state.token !== token) {
          console.log('Auth mismatch detected - clearing storage');
          needsClear = true;
        }
      } catch (e) {
        // JSON 파싱 에러 - 손상된 데이터
        console.log('Auth data corrupted - clearing storage');
        needsClear = true;
      }
    } else if ((token && !authStorage) || (!token && authStorage)) {
      // 둘 중 하나만 있는 경우 - 부분 삭제됨
      console.log('Partial auth data detected - clearing storage');
      needsClear = true;
    }
    
    // 불일치 시 전체 클리어 후 리로드
    if (needsClear) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
      return;
    }
    
    // 정상적인 경우에만 Socket 연결
    if (token) {
      socketService.connect(token);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          {/* 고객 URL 접속 */}
          <Route path="/:projectId/:password" element={<CustomerAccess />} />
          
          {/* 고객 홈 직접 접속 URL */}
          <Route path="/h/:projectId/:password" element={<CustomerAccess directToHome={true} />} />
          
          {/* 고객 소통 직접 접속 URL */}
          <Route path="/s/:projectId/:password" element={<CustomerAccess directToChat={true} />} />
          
          {/* 로그인 */}
          <Route path="/login" element={<Login />} />
          
          {/* 비회원도 접속 가능한 페이지 */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contractor/apply" element={<ContractorApplication />} />
          
          {/* 인증이 필요한 라우트 */}
          <Route element={<PrivateRoute />}>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<ProjectList />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/p/:id" element={<ProjectDetail />} />
              <Route path="/s/:id" element={<ProjectDetail directToChat={true} />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/profile/change-password" element={<ChangePassword />} />
              <Route path="/profile/notifications" element={<NotificationSettings />} />
              <Route path="/notices" element={<Notice />} />
              <Route path="/notices/:id" element={<Notice />} />
              <Route path="/help" element={<Help />} />
              <Route path="/contractors" element={<ContractorList />} />
              <Route path="/contractors/:id" element={<ContractorProfile />} />
              <Route path="/contractors/:id/stats" element={<ContractorStatsView />} />
              <Route path="/contractor/stats" element={<ContractorStats />} />
              <Route path="/contractor/commission" element={<ContractorCommission />} />
              <Route path="/my-contractor" element={<MyContractor />} />
              <Route path="/notifications" element={<NotificationList />} />
              <Route path="/admin/*" element={<AdminDashboard />} />
              <Route path="/admin/contractor-applications" element={<AdminContractorApplications />} />
              <Route path="/vip/*" element={<VipDashboard />} />
            </Route>
          </Route>
        </Routes>
      </Router>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#333',
            color: '#fff',
          },
        }}
      />
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
        limit={3}
      />
    </QueryClientProvider>
  );
}

export default App;